/**
 * A sent application's answers, read-only: personal details, health, doctors, medications and
 * beneficiaries as they were sent. Nothing here can be edited.
 */
import { DetailList, type DetailRow } from './figures';

const YES_NO: Record<ConsumerYesNo, string> = { 'yes': 'Yes', 'no': 'No', '': 'Not answered' };

const orDash = (value: string | number | null | undefined) =>
  value === null || value === undefined || value === '' ? '—' : String(value);

function Section({ title, rows }: { title: string; rows: DetailRow[] }) {
  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-sm font-semibold text-gray-950">{title}</h3>
      {rows.length ? (
        <DetailList rows={rows} />
      ) : (
        <p className="text-muted-foreground text-sm">None listed</p>
      )}
    </section>
  );
}

export function ApplicationAnswersSummary({ answers }: { answers: ConsumerApplicationAnswers }) {
  const { personal, health } = answers;
  const height =
    personal.heightFeet === null
      ? null
      : `${personal.heightFeet} ft ${personal.heightInches ?? 0} in`;

  return (
    <div
      className="flex flex-col gap-4"
      data-testid="application-answers-summary"
    >
      <Section
        title="Personal details"
        rows={[
          { label: 'Name', value: orDash(`${personal.firstName} ${personal.lastName}`.trim()) },
          { label: 'Date of birth', value: orDash(personal.dateOfBirth) },
          {
            label: 'Address',
            value: orDash(
              [personal.street, personal.city, personal.state, personal.zip]
                .filter(Boolean)
                .join(', '),
            ),
          },
          { label: 'Phone', value: orDash(personal.phone) },
          { label: 'Email', value: orDash(personal.email) },
          { label: 'Height', value: orDash(height) },
          {
            label: 'Weight',
            value: personal.weightPounds === null ? '—' : `${personal.weightPounds} lb`,
          },
        ]}
      />
      <Section
        title="Health"
        rows={[
          { label: 'Conditions', value: orDash(health.conditions) },
          { label: 'Tobacco', value: YES_NO[health.tobacco] },
          { label: 'Help with daily activities', value: YES_NO[health.dailyActivityHelp] },
          { label: 'Memory diagnosis', value: YES_NO[health.memoryDiagnosis] },
          { label: 'Recent hospital stay', value: YES_NO[health.recentHospitalStay] },
          { label: 'Pending tests', value: YES_NO[health.pendingTests] },
        ]}
      />
      <Section
        title="Doctors"
        rows={answers.doctors.map(doctor => ({
          label: orDash(doctor.name),
          value: [doctor.practice, doctor.phone].filter(Boolean).join(' · ') || '—',
        }))}
      />
      <Section
        title="Medications"
        rows={answers.medications.map(medication => ({
          label: orDash(medication.name),
          value: [medication.reason, medication.dose].filter(Boolean).join(' · ') || '—',
        }))}
      />
      {answers.beneficiaries.length > 0 && (
        <Section
          title="Beneficiaries"
          rows={answers.beneficiaries.map(beneficiary => ({
            label: orDash(beneficiary.fullName),
            value:
              [
                beneficiary.relationship,
                beneficiary.sharePercent === null ? null : `${beneficiary.sharePercent}%`,
              ]
                .filter(Boolean)
                .join(' · ') || '—',
          }))}
        />
      )}
    </div>
  );
}
